import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { useCachedImage } from '@renderer/hooks/useCachedImage'

const Camera: React.FC = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { setSessionState, uploadSessionState, logActivity } = useSessionStateStore()
  const { config } = useConfigStore()
  const { selectedSecondaryCamera } = useSettingsStore()
  const [selectedMode, setSelectedMode] = useState<'basic' | 'basic-high' | 'high' | null>(null)

  // Check if secondary camera is available
  const isSecondaryCameraAvailable = Boolean(selectedSecondaryCamera)

  console.log('isSecondaryCameraAvailable', isSecondaryCameraAvailable)
  console.log('selectedSecondaryCamera', selectedSecondaryCamera)

  const cameraOptions = [
    {
      id: 'basic' as const,
      image: '/assets/yotta/camera-basic.webp',
      label: 'Basic',
      isAvailable: true
    },
    {
      id: 'basic-high' as const,
      image: '/assets/yotta/camera-basichigh.webp',
      label: 'Basic High',
      isAvailable: isSecondaryCameraAvailable
    },
    {
      id: 'high' as const,
      image: '/assets/yotta/camera-high-angle.webp',
      label: 'High Angle',
      isAvailable: isSecondaryCameraAvailable
    }
  ]

  const handleModeSelect = (mode: 'basic' | 'basic-high' | 'high'): void => {
    if (!cameraOptions.find((opt) => opt.id === mode)?.isAvailable) {
      return // Prevent selection of unavailable modes
    }

    setSelectedMode(mode)

    // Determine the camera to start with based on mode
    const initialCamera = mode === 'high' ? 'secondary' : 'primary'

    setSessionState({
      mode: 'capture', // This is a capture session
      userPreferences: {
        cameraMode: mode,
        captureSequence: mode === 'basic-high' ? ['primary', 'secondary'] : [initialCamera],
        currentCaptureStep: 0,
        selectedCamera: initialCamera
      },
      timeline: {
        activityLogs: [
          {
            page: 'camera',
            type: 'selection',
            action: 'select',
            details: {
              cameraMode: mode,
              requiresSecondaryCamera: mode === 'basic-high' || mode === 'high',
              captureCount: mode === 'basic-high' ? 2 : 1,
              initialCamera: initialCamera
            },
            timestamp: new Date()
          }
        ]
      }
    })
    logActivity(`Camera mode selected: ${mode}, starting with ${initialCamera} camera`)
  }

  const handleNext = (): void => {
    if (!selectedMode) return

    logActivity(`Proceeding to frame selection with camera mode: ${selectedMode}`)

    uploadSessionState()
    const nextPage = config?.pages?.frame?.settings?.enabled ? 'frame' : 'checkout'
    navigate(`/${projectId}/${nextPage}`)
  }

  return (
    <div className="w-full h-full bg-white flex flex-col">
      {/* Title */}
      <div className="text-center pt-16 pb-8">
        <h1 className="font-title-yotta text-blueGrayYotta text-[96px] font-bold drop-shadow-lg">
          Camera
        </h1>
      </div>

      {/* Camera Options */}
      <div className="flex-1 flex items-center justify-center px-16 mb-32">
        <div className="grid grid-cols-3 gap-12 w-full max-w-6xl">
          {cameraOptions.map((option) => (
            <button
              key={option.id}
              onClick={() => handleModeSelect(option.id)}
              disabled={!option.isAvailable}
              className={`relative group transition-all duration-300 ${
                !option.isAvailable
                  ? 'opacity-50 cursor-not-allowed grayscale'
                  : selectedMode === option.id
                    ? 'scale-105 shadow-2xl'
                    : 'hover:scale-102 hover:shadow-xl'
              }`}
            >
              <div
                className={`relative overflow-hidden rounded-lg border-4 transition-all duration-300 ${
                  selectedMode === option.id
                    ? 'border-blueGrayYotta'
                    : option.isAvailable
                      ? 'border-gray-200 group-hover:border-gray-300'
                      : 'border-gray-300'
                }`}
              >
                <CachedCameraOptionImage image={option.image} label={option.label} />

                {/* Unavailable Overlay */}
                {!option.isAvailable && (
                  <div className="absolute inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center">
                    <div className="bg-white/90 rounded-lg px-4 py-2">
                      <span className="text-gray-700 text-sm font-medium">
                        Secondary Camera Required
                      </span>
                    </div>
                  </div>
                )}

                {/* Selection Overlay */}
                {selectedMode === option.id && option.isAvailable && (
                  <div className="absolute inset-0 bg-blueGrayYotta bg-opacity-20 flex items-center justify-center">
                    <div className="bg-white rounded-full p-3">
                      <svg
                        className="w-8 h-8 text-blueGrayYotta"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Next Button */}
      <div className="absolute bottom-12 right-24">
        <button
          onClick={handleNext}
          disabled={!selectedMode}
          className={`font-body-yotta text-blueGrayYotta text-[32px] hover:opacity-80 transition-opacity active:scale-95 transform ${
            !selectedMode ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          Next →
        </button>
      </div>
    </div>
  )
}

// Component for cached camera option images
const CachedCameraOptionImage: React.FC<{ image: string; label: string }> = ({ image, label }) => {
  const cachedSrc = useCachedImage(image)
  return <img src={cachedSrc} alt={label} className="w-full h-auto object-cover" />
}

export default Camera
